export type AuroraLevel = 'green' | 'yellow' | 'amber' | 'red';

export interface AuroraRating {
  level: AuroraLevel;
  hex: string;
  label: string;
  description: string;
  guestAdvice: string;
}

export const AURORA_RATINGS: Record<AuroraLevel, AuroraRating> = {
  green: {
    level: 'green',
    hex: '#22C55E',
    label: 'Low',
    description: 'Geomagnetic activity is quiet. Aurora unlikely tonight.',
    guestAdvice:
      'Enjoy the stars - the Lapland sky is spectacular even without aurora. Check back later as conditions can change rapidly.',
  },
  yellow: {
    level: 'yellow',
    hex: '#EAB308',
    label: 'Moderate',
    description: 'Some geomagnetic activity detected. Faint aurora possible.',
    guestAdvice:
      'Worth stepping outside after dark if skies are clear. Look north and be patient - aurora can appear suddenly. Best viewing 10pm-2am.',
  },
  amber: {
    level: 'amber',
    hex: '#F97316',
    label: 'High',
    description: 'Significant geomagnetic activity. Good chance of visible aurora.',
    guestAdvice:
      "Head outside! Clear skies and dark locations away from village lights give the best views. Your cabin's hot tub is an ideal viewing spot.",
  },
  red: {
    level: 'red',
    hex: '#EF4444',
    label: 'Very High',
    description: 'Strong geomagnetic storm. Bright, vivid aurora very likely.',
    guestAdvice:
      'Drop everything and get outside NOW. This is a rare, powerful display - the sky could be alive with colour. Set your phone camera to night mode.',
  },
};

export function getAuroraLevel(kp: number, fmiRIndex?: number): AuroraLevel {
  let level: AuroraLevel = 'green';

  if (kp >= 7) level = 'red';
  else if (kp >= 5) level = 'amber';
  else if (kp >= 3) level = 'yellow';

  if (fmiRIndex !== undefined) {
    if (fmiRIndex > 500 && level !== 'red') level = 'red';
    else if (fmiRIndex > 200 && level === 'green') level = 'yellow';
    else if (fmiRIndex > 200 && level === 'yellow') level = 'amber';
  }

  return level;
}

export function getKpTrend(values: number[]): 'rising' | 'falling' | 'stable' {
  if (values.length < 2) return 'stable';
  const last = values[values.length - 1];
  const prev = values[values.length - 2];
  const diff = last - prev;
  if (diff > 0.5) return 'rising';
  if (diff < -0.5) return 'falling';
  return 'stable';
}

export function isDarkInLapland(): boolean {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed
  // Rough estimate: no darkness May-July at 67-68°N
  if (month >= 4 && month <= 6) return false;
  // Partial darkness in April and August
  const hour = now.getUTCHours();
  if (month === 3 || month === 7) return hour >= 19 || hour < 4;
  // Sep-Mar: dark evening/night hours (UTC)
  return hour >= 15 || hour < 7;
}

export function getLaplandProbability(
  coordinates: [number, number, number][],
): number {
  // Filter for Lapland area: lat 66.5-69, lon 22-26
  const laplandPoints = coordinates.filter(
    ([lon, lat]) => lat >= 66.5 && lat <= 69 && lon >= 22 && lon <= 26,
  );
  if (laplandPoints.length === 0) return 0;
  const sum = laplandPoints.reduce((acc, [, , prob]) => acc + prob, 0);
  return Math.round(sum / laplandPoints.length);
}

export interface KpForecastPoint {
  time: string;
  kp: number;
  level: AuroraLevel;
  type: 'observed' | 'estimated' | 'predicted';
}

export interface AuroraData {
  level: AuroraLevel;
  label: string;
  description: string;
  guestAdvice: string;
  hex: string;
  kp: {
    current: number;
    trend: 'rising' | 'falling' | 'stable';
    forecast: KpForecastPoint[];
  };
  fmi: {
    station: string;
    rIndex: number | null;
    activity: 'quiet' | 'moderate' | 'high' | 'storm';
    stations: {
      code: string;
      name: string;
      lat: number;
      lon: number;
      rIndex: number | null;
      activity: 'quiet' | 'moderate' | 'high' | 'storm';
    }[];
  };
  ovation: {
    laplandProbability: number;
    forecastTime: string;
    mapImageUrl: string;
  };
  cloudCover: number | null;
  isDark: boolean;
  updatedAt: string;
}

export const MAGNETOMETER_STATIONS = [
  { code: 'MUO', name: 'Muonio', lat: 68.02, lon: 23.53 },
  { code: 'PEL', name: 'Pello', lat: 66.90, lon: 24.08 },
] as const;

export type MagnetometerStation = (typeof MAGNETOMETER_STATIONS)[number];

export function getRIndexActivity(rVal: number): 'quiet' | 'moderate' | 'high' | 'storm' {
  if (rVal > 500) return 'storm';
  if (rVal > 200) return 'high';
  if (rVal > 50) return 'moderate';
  return 'quiet';
}

export const AURORA_CALENDAR = [
  { month: 'Sep', darkHours: '8-10h', season: true, typicalKp: '2-4', notes: 'Autumn equinox boosts activity' },
  { month: 'Oct', darkHours: '12-14h', season: true, typicalKp: '2-5', notes: 'Reliable dark skies' },
  { month: 'Nov', darkHours: '18-20h', season: true, typicalKp: '2-5', notes: 'Very long dark nights' },
  { month: 'Dec', darkHours: '22-24h', season: true, typicalKp: '2-4', notes: 'Polar night - max darkness' },
  { month: 'Jan', darkHours: '20-22h', season: true, typicalKp: '2-4', notes: 'Coldest, clearest skies' },
  { month: 'Feb', darkHours: '14-16h', season: true, typicalKp: '2-5', notes: 'Returning light, still dark enough' },
  { month: 'Mar', darkHours: '10-12h', season: true, typicalKp: '2-5', notes: 'Spring equinox boosts activity' },
  { month: 'Apr', darkHours: '6-8h', season: false, typicalKp: '2-3', notes: 'Too light most nights' },
  { month: 'May', darkHours: '0-2h', season: false, typicalKp: '-', notes: 'Midnight sun - no darkness' },
  { month: 'Jun', darkHours: '0h', season: false, typicalKp: '-', notes: 'Midnight sun' },
  { month: 'Jul', darkHours: '0-2h', season: false, typicalKp: '-', notes: 'Midnight sun' },
  { month: 'Aug', darkHours: '2-4h', season: false, typicalKp: '-', notes: 'Season not yet started' },
];
